"use client";

import { useCallback, useState, type FormEvent } from "react";

import type { KundaliField } from "@/lib/analytics-kundali";
import { isValidOptionalEmail } from "@/lib/phone-e164";
import { isContactGateSatisfied } from "./KundaliFormCard.validation";
import type { KundaliLang } from "./KundaliFormCard.i18n";
import {
  progressiveStepForErrors,
  progressiveStepForField,
  type KundaliProgressiveCopy,
  type KundaliProgressiveStep,
} from "./KundaliFormCard.progressive";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import { useKundaliProgressiveTelemetry } from "./useKundaliProgressiveTelemetry";

type Options = {
  progressiveCopy?: KundaliProgressiveCopy;
  initialFocusField?: KundaliField;
  fieldErrors: KundaliFieldErrors;
  language: KundaliLang;
  source: string;
  sourcePath?: string;
  phone: string;
  email: string;
  submitDisabled?: boolean;
  onSubmit: (event: FormEvent) => void;
};

export function useProgressiveKundaliFormState(options: Options) {
  const [step, setStep] = useState<KundaliProgressiveStep>(() =>
    options.initialFocusField ? progressiveStepForField(options.initialFocusField) : 0
  );
  const errorStep = options.progressiveCopy ? progressiveStepForErrors(options.fieldErrors) : null;
  const visibleStep = errorStep ?? step;
  const trackAction = useKundaliProgressiveTelemetry({
    enabled: Boolean(options.progressiveCopy),
    step: visibleStep,
    language: options.language,
    source: options.source,
    sourcePath: options.sourcePath,
  });
  const submit = useCallback(
    (event: FormEvent) => {
      if (isCompleteContactStep(options, visibleStep)) trackAction("complete", visibleStep);
      options.onSubmit(event);
    },
    [options, trackAction, visibleStep]
  );
  return {
    visibleStep,
    showFinalAction: !options.progressiveCopy || visibleStep === 2,
    setProgressiveStep: setStep,
    trackProgressiveAction: trackAction,
    submit,
  };
}

function isCompleteContactStep(options: Options, step: KundaliProgressiveStep) {
  return Boolean(
    options.progressiveCopy &&
    step === 2 &&
    !options.submitDisabled &&
    isContactGateSatisfied(options.phone) &&
    isValidOptionalEmail(options.email)
  );
}
